import { useEffect, useState } from 'react';
import { Patient } from '@models/patient';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import {
  useAccountCreate,
  useOrganizationFindAllPatients,
} from '../../../libs/ohat-api';
import {
  App,
  Button,
  Card,
  Col,
  ConfigProvider,
  Dropdown,
  Flex,
  Row,
  Space,
} from 'antd';
import { HomePageBreadCrumb } from '../../components/breadcrumb/HomePageBreadCrumb';
import { OrganizationDetailPanel } from '../../components/OrganizationDetailPanel';
import { PatientListPanel } from './PatientListPanel';
import {
  DownloadOutlined,
  EllipsisOutlined,
  SearchOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { usePatientCsvDownload } from '../../../hooks/usePatientCsvDownload';
import useMobile from '../../../hooks/useMobile';

/**
 * 詳細検索ボタンの色。
 * アプリ全体の colorPrimary（#003288 濃紺）は沈んで見えるため、このボタンだけ
 * グラフ等で使用済みの明るい青を使う（アプリ内の色数を増やさない）。
 */
const ADVANCED_SEARCH_COLOR = '#1E88E5';

/**
 * 患者一覧が未取得のときに渡す空配列。
 * `?? []` をJSX内に直接書くとレンダリングのたびに新しい参照になり、
 * PatientListPanel 側の useEffect が毎回発火して無限ループになるため定数化する。
 */
const EMPTY_PATIENTS: Patient[] = [];

export function HomePage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const { mutateAsync: createAccount } = useAccountCreate();
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const { download: downloadCsv, loading: csvLoading } =
    usePatientCsvDownload(filteredPatients);
  // suspenseを使うと、ErrorBoundaryで処理されてしまうので、ここだけは使わない
  const { data: org } = useOrganizationFindAllPatients(
    auth.account?.organizationId ?? '',
  );

  const showModal = () => {
    modal.info({
      content: t('home.setupPrompt'),
      onOk: () => {
        navigate({ pathname: '/organizations/create' });
      },
    });
  };

  const recoveryAccount = async (email: string, sub: string) => {
    try {
      const apiResult = await createAccount({
        data: {
          email,
          sub,
        },
      });
      if (apiResult) await auth.fetchAccount(sub);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    if (!auth.account) {
      // cognitoアカウントは存在するが、紐づくAccountレコードが存在しない場合はログアウト
      if (auth.rawData?.signInDetails?.loginId) {
        recoveryAccount(
          auth.rawData.signInDetails.loginId,
          auth.rawData.username,
        );
      }
    }
    if (auth.account && !auth.account.organization) {
      showModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  // 以下3つのボタンは PC（Card の extra）とモバイル（Card 本文の先頭）の
  // 双方から使うため、定義はここに1箇所だけ置く。
  /** 患者追加ボタン。組織情報パネルから移動し、操作対象である患者一覧の近くに置く。 */
  const addPatientButton = (props: { block?: boolean } = {}) => (
    <Button
      type="primary"
      shape="round"
      size="large"
      icon={<UserAddOutlined />}
      block={props.block}
      onClick={() => navigate('/patients/create')}
    >
      {t('org.addPatient')}
    </Button>
  );

  /**
   * 詳細検索ボタン。
   * このボタンだけ colorPrimary を上書きする。
   * style で background/borderColor を直接指定するとホバー・アクティブ時の
   * 色が antd 側で導出されず破綻するため、シード（colorPrimary）を差し替えて
   * 各状態色を antd に導出させる。グローバルのテーマ設定は変更しない。
   */
  const advancedSearchButton = (props: { block?: boolean } = {}) => (
    <ConfigProvider theme={{ token: { colorPrimary: ADVANCED_SEARCH_COLOR } }}>
      <Button
        type="primary"
        shape="round"
        size="large"
        icon={<SearchOutlined />}
        block={props.block}
        onClick={() => setShowSearchForm(!showSearchForm)}
      >
        {t('home.advancedSearch')}
      </Button>
    </ConfigProvider>
  );

  /**
   * 「…」メニュー。
   * CSVダウンロードは単独ボタンだとヘッダーの横幅を圧迫するため「…」メニューに
   * 格納する。モバイルでも同じUIで利用できる。org 未取得時は表示しない。
   */
  const moreActionsDropdown = org ? (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{
        items: [
          {
            key: 'csv',
            icon: <DownloadOutlined />,
            label: t('csv.download'),
            disabled: csvLoading,
            onClick: () => {
              void downloadCsv();
            },
          },
        ],
      }}
    >
      <Button
        shape="round"
        size="large"
        icon={<EllipsisOutlined />}
        loading={csvLoading}
        aria-label={t('csv.download')}
      />
    </Dropdown>
  ) : null;

  return (
    <Row gutter={[16, 8]} style={{ flex: 1, maxWidth: 1200 }}>
      <Col md={24} xs={24}>
        <HomePageBreadCrumb />
      </Col>
      <Col md={8} xs={24}>
        <Card
          title={t('home.orgInfo')}
          extra={
            org && (
              <Link to={`organizations/${org.id}/edit`}>
                {t('home.orgEdit')}
              </Link>
            )
          }
        >
          {org && <OrganizationDetailPanel organization={org} />}
        </Card>
      </Col>
      <Col md={16} xs={24}>
        <Card
          title={t('home.patientList')}
          extra={
            isMobile() ? (
              moreActionsDropdown
            ) : (
              <Space wrap>
                {addPatientButton()}
                {advancedSearchButton()}
                {moreActionsDropdown}
              </Space>
            )
          }
        >
          {/* モバイルではヘッダー（タイトル横）に3つのボタンを並べると窮屈に折り返すため、
              ヘッダー右端には「…」ボタンだけを残し、「患者追加」と「詳細検索」は
              本文の先頭に全幅で縦積みする（デザイン準拠）。 */}
          {isMobile() && (
            <Flex vertical gap={8} style={{ marginBottom: 16 }}>
              {addPatientButton({ block: true })}
              {advancedSearchButton({ block: true })}
            </Flex>
          )}
          <PatientListPanel
            patients={org?.patients ?? EMPTY_PATIENTS}
            showSearchForm={showSearchForm}
            setShowSearchForm={setShowSearchForm}
            onFilteredPatientsChange={setFilteredPatients}
          />
        </Card>
      </Col>
    </Row>
  );
}
